import { useState } from 'react'

import { DocumentNode } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ChangeQuoteStatusDocument,
  FinanceEnumsQuoteStatusEnum,
  QuoteDetailQuery,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import handleResultWithFlash from 'modules/finance/lib/handleResultWithFlash'

function isValidQuoteStatus(value: string): value is FinanceEnumsQuoteStatusEnum {
  return ['accepted', 'draft', 'rejected'].includes(value)
}

export const useChangeStatusQuoteDialog = (options?: {
  refetchQueries?: Array<string | DocumentNode>
}) => {
  const [showModal, setShowModal] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<FinanceEnumsQuoteStatusEnum | undefined>(
    undefined
  )
  const [currentQuote, setCurrentQuote] = useState<QuoteDetailQuery['finance']['quotes'][0] | null>(
    null
  )
  const [callbackFn, setCallbackFn] = useState<(() => void) | undefined>(undefined)

  const [changeStatus, { loading }] = useMutation(ChangeQuoteStatusDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceQuote,
    ...(options?.refetchQueries ? { refetchQueries: options.refetchQueries } : {}),
  })

  const showChangeStatusQuoteDialog = ({
    quote,
    callback,
  }: {
    quote: QuoteDetailQuery['finance']['quotes'][0]
    callback?: () => void
  }) => {
    setCurrentQuote(quote)
    setSelectedStatus(quote.status)
    setCallbackFn(() => callback)
    setShowModal(true)
  }

  const handleClose = () => {
    setShowModal(false)
    setCurrentQuote(null)
    setCallbackFn(undefined)
  }

  const handleSubmit = async () => {
    if (!currentQuote || !selectedStatus) return
    const result = await changeStatus({
      variables: {
        id: currentQuote.id,
        status: selectedStatus,
      },
    })
    const handled = handleResult(result, handleResultWithFlash(handleClose))
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: 'Status changed successfully',
      })
      if (callbackFn) callbackFn()
      setShowModal(false)
    }
  }

  const statusOptions = [
    { label: 'Accepted', value: 'accepted' },
    { label: 'Draft', value: 'draft' },
    { label: 'Rejected', value: 'rejected' },
  ]

  const modal = showModal && (
    <Modal
      title={'Change status'}
      description={'Select a different status for this quote'}
      illustration="pencil"
      onSubmit={handleSubmit}
      onClose={handleClose}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={handleClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={loading || !selectedStatus}>
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      <Select
        value={selectedStatus}
        label={'Status'}
        options={statusOptions}
        onChange={(x) => isValidQuoteStatus(x) && setSelectedStatus(x)}
      />
    </Modal>
  )

  return { showChangeStatusQuoteDialog, modal }
}
